// src/pages/butcher/butcherpanel/InquiriesPanel.jsx
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../../utils/api';

const AUTH = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `http://localhost:4000${url}`;
};

const STATUS = {
  open:    { label: 'Open',    color: 'bg-amber-100 text-amber-700'     },
  replied: { label: 'Replied', color: 'bg-emerald-100 text-emerald-700' },
  closed:  { label: 'Closed',  color: 'bg-gray-100 text-gray-500'       },
};

// ── Card: inquiries butcher SENT to farmers about animals ──
function SentCard({ inquiry }) {
  const st = STATUS[inquiry.status] || STATUS.open;
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-outline-variant/10
                    p-4 space-y-3 hover:border-primary/20 transition-all">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl overflow-hidden bg-surface-container flex-shrink-0
                        flex items-center justify-center">
          {inquiry.image_url
            ? <img src={getImageUrl(inquiry.image_url)} alt={inquiry.animal_name}
                   className="w-full h-full object-cover" />
            : <span className="material-symbols-outlined text-outline text-[22px]">pets</span>
          }
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-bold text-sm text-on-surface">{inquiry.animal_name || '—'}</p>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${st.color}`}>
              {st.label}
            </span>
          </div>
          <p className="text-[11px] text-secondary">
            To: <span className="font-bold">{inquiry.seller_name || 'Farmer'}</span>
          </p>
          <div className="flex items-center gap-2 flex-wrap mt-0.5">
            <span className="text-[10px] font-mono text-outline bg-surface-container px-2 py-0.5 rounded-full">
              Inquiry #{inquiry.id}
            </span>
            <span className="text-[10px] font-mono text-outline bg-surface-container px-2 py-0.5 rounded-full">
              Animal #{inquiry.animal_id}
            </span>
            <span className="text-[10px] text-outline">
              {new Date(inquiry.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              {' · '}
              {new Date(inquiry.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        </div>
      </div>

      <div className="bg-surface-container-low rounded-xl p-3">
        <p className="text-[10px] font-black uppercase text-secondary mb-1">Your Message</p>
        <p className="text-sm text-on-surface">{inquiry.message}</p>
      </div>

      {inquiry.reply ? (
        <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3">
          <p className="text-[10px] font-black uppercase text-emerald-700 mb-1">Farmer's Reply</p>
          <p className="text-sm text-on-surface">{inquiry.reply}</p>
        </div>
      ) : (
        <p className="text-xs text-secondary italic flex items-center gap-1">
          <span className="material-symbols-outlined text-[14px]">hourglass_empty</span>
          Waiting for farmer's reply…
        </p>
      )}
    </div>
  );
}

// ── Card: inquiries butcher RECEIVED about meat products ───
function ReceivedCard({ inquiry, onReplySuccess }) {
  const [showReply, setShowReply] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending,   setSending]   = useState(false);
  const st = STATUS[inquiry.status] || STATUS.open;

  const handleReply = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      await api.post(
        `/api/butcher/inquiries/${inquiry.id}/reply`,
        { reply: replyText }
      );
      setReplyText('');
      setShowReply(false);
      onReplySuccess();
    } catch (err) {
      alert(err.response?.data?.error || 'Could not send reply');
    } finally { setSending(false); }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-outline-variant/10
                    p-4 space-y-3 hover:border-primary/20 transition-all">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl overflow-hidden bg-surface-container flex-shrink-0
                        flex items-center justify-center">
          {inquiry.product_image
            ? <img src={getImageUrl(inquiry.product_image)} alt={inquiry.product_name}
                   className="w-full h-full object-cover" />
            : <span className="material-symbols-outlined text-outline text-[22px]">storefront</span>
          }
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-bold text-sm text-on-surface">{inquiry.product_name || '—'}</p>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-600">
              🥩 Meat
            </span>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${st.color}`}>
              {st.label}
            </span>
          </div>
          <p className="text-[11px] text-secondary">
            From: <span className="font-bold">{inquiry.sender_name || 'Customer'}</span>
            {inquiry.sender_phone && (
              <a href={`tel:${inquiry.sender_phone}`}
                className="ml-2 text-primary hover:underline inline-flex items-center gap-0.5">
                <span className="material-symbols-outlined text-[12px]">call</span>
                {inquiry.sender_phone}
              </a>
            )}
          </p>
          <div className="flex items-center gap-2 flex-wrap mt-0.5">
            <span className="text-[10px] font-mono text-outline bg-surface-container px-2 py-0.5 rounded-full">
              Inquiry #{inquiry.id}
            </span>
            <span className="text-[10px] font-mono text-outline bg-surface-container px-2 py-0.5 rounded-full">
              Product #{inquiry.product_id}
            </span>
            <span className="text-[10px] text-outline">
              {new Date(inquiry.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              {' · '}
              {new Date(inquiry.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        </div>
      </div>

      <div className="bg-surface-container-low rounded-xl p-3">
        <p className="text-[10px] font-black uppercase text-secondary mb-1">Their Message</p>
        <p className="text-sm text-on-surface">{inquiry.message}</p>
      </div>

      {inquiry.reply && (
        <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3">
          <p className="text-[10px] font-black uppercase text-emerald-700 mb-1">Your Reply</p>
          <p className="text-sm text-on-surface">{inquiry.reply}</p>
        </div>
      )}

      {showReply ? (
        <div className="space-y-2">
          <textarea
            value={replyText}
            onChange={e => setReplyText(e.target.value)}
            placeholder="Type your reply…"
            rows={3}
            className="w-full px-3 py-2 rounded-xl border border-outline-variant/40 text-sm
                       outline-none focus:border-primary resize-none transition-all"
          />
          <div className="flex gap-2">
            <button onClick={handleReply} disabled={sending || !replyText.trim()}
              className="px-4 py-2 rounded-xl bg-primary text-white text-xs font-bold
                         hover:opacity-90 disabled:opacity-50 transition-all">
              {sending ? 'Sending…' : 'Send Reply'}
            </button>
            <button onClick={() => setShowReply(false)}
              className="px-4 py-2 rounded-xl border border-outline-variant text-secondary
                         text-xs font-semibold hover:bg-surface-container transition-all">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button onClick={() => setShowReply(true)}
          className="px-4 py-2 rounded-xl border border-primary text-primary text-xs
                     font-bold hover:bg-primary hover:text-white transition-all">
          {inquiry.reply ? '✏️ Update Reply' : 'Reply'}
        </button>
      )}
    </div>
  );
}

// ── Main Panel ─────────────────────────────────────────────
export default function InquiriesPanel() {
  const [tab,      setTab]      = useState('received');
  const [sent,     setSent]     = useState([]);
  const [received, setReceived] = useState([]);
  const [loading,  setLoading]  = useState(true);

  const fetchAll = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get('/api/butcher/inquiries/sent'),
      api.get('/api/butcher/inquiries/received'),
    ]).then(([sentRes, receivedRes]) => {
      setSent(sentRes.data);
      setReceived(receivedRes.data);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const data = tab === 'sent' ? sent : received;

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-black text-on-surface">Inquiries</h2>
        <p className="text-sm text-secondary mt-1">
          Manage your sent and received inquiries.
        </p>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-2 bg-white rounded-2xl p-1.5 shadow-sm
                      border border-outline-variant/10 w-fit">
        <button onClick={() => setTab('received')}
          className={`px-5 py-2 rounded-xl text-sm font-bold transition-all ${
            tab === 'received'
              ? 'bg-primary text-white'
              : 'text-secondary hover:bg-surface-container'
          }`}>
          🥩 Received
          <span className="ml-1.5 text-[11px] opacity-80">({received.length})</span>
        </button>
        <button onClick={() => setTab('sent')}
          className={`px-5 py-2 rounded-xl text-sm font-bold transition-all ${
            tab === 'sent'
              ? 'bg-primary text-white'
              : 'text-secondary hover:bg-surface-container'
          }`}>
          🐐 Sent to Farmers
          <span className="ml-1.5 text-[11px] opacity-80">({sent.length})</span>
        </button>
      </div>

      {/* Stats pills */}
      {data.length > 0 && (
        <div className="flex gap-3 flex-wrap">
          {[
            { label: 'Total',   val: data.length,                                       color: 'bg-white border border-outline-variant/20 text-secondary' },
            { label: 'Open',    val: data.filter(i => i.status === 'open').length,    color: 'bg-amber-50 text-amber-700'     },
            { label: 'Replied', val: data.filter(i => i.status === 'replied').length, color: 'bg-emerald-50 text-emerald-700' },
          ].map(p => (
            <div key={p.label}
                 className={`px-4 py-2 rounded-xl text-sm font-bold shadow-sm ${p.color}`}>
              {p.val} {p.label}
            </div>
          ))}
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="py-16 flex justify-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent
                          rounded-full animate-spin" />
        </div>
      ) : data.length === 0 ? (
        <div className="bg-white rounded-2xl py-16 text-center shadow-sm
                        border border-outline-variant/10">
          <span className="material-symbols-outlined text-5xl text-outline-variant">forum</span>
          <p className="mt-3 font-bold text-on-surface">
            {tab === 'received' ? 'No inquiries received yet' : 'No inquiries sent yet'}
          </p>
          <p className="text-xs text-secondary mt-1">
            {tab === 'received'
              ? 'Customer messages about your meat products will appear here.'
              : 'Your messages to farmers about animals will appear here.'
            }
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {tab === 'sent'
            ? sent.map(inq     => <SentCard     key={inq.id} inquiry={inq} />)
            : received.map(inq => <ReceivedCard key={inq.id} inquiry={inq}
                                                onReplySuccess={fetchAll} />)
          }
        </div>
      )}
    </div>
  );
}